import { datadogLogs } from '@datadog/browser-logs';
import { type Level as PinoLevel } from 'pino';
import platform from 'platform';
import { admitLogLine, buildLogSignature } from './burst-throttle';
import { getLoggerConfig } from './config';
import { originalConsoleMethods } from './console-interceptor';
import { isAxiosError } from './sanitize-error';
import {
  createScrubStats,
  scrubCredentials,
  scrubCredentialString,
  type ScrubStats,
} from './scrub-credentials';
import { type LogContext } from './types';

/**
 * Datadog TRANSMIT path only — `datadogLogs.init` stays app-side
 * (src/utils/tracing.ts) and never moves into this package. Transmission is
 * gated on the injected `enabled` flag (ON-PREM-GATE, G1): a present/leaked
 * Datadog configuration is never sufficient to emit.
 *
 * This is also the single chokepoint where the per-signature burst cap
 * applies (see ./burst-throttle), so one client stuck in a loop cannot
 * dominate ingest for the whole fleet.
 */
let failedSinceLastLine = 0;

export function __resetDatadogTransmitForTests(): void {
  failedSinceLastLine = 0;
}

export function sendToDatadog(
  level: PinoLevel,
  message: unknown,
  context?: LogContext
): void {
  const { enabled, isProd, bundleId, gitCommit, deploymentId } = getLoggerConfig();
  if (!enabled || !isDatadogAvailable()) {
    return;
  }

  try {
    // The budget check comes FIRST, ahead of both the transmit formatting and
    // the context assembly, so a dropped line pays only for its signature.
    // Ordering matters on the hot path this exists to shed: formatting the
    // message is a full `JSON.stringify` for object and Error payloads, and
    // `buildLogSignature` derives its own text anyway, so computing
    // `messageForDatadog` above this point made every dropped object line
    // serialize twice and discard both.
    //
    // The signature is built from the ORIGINAL message, never the formatted
    // string. An Error is transmitted as its bare classification, so signing
    // the transmitted text would put every `logger.error(err)` site in the app
    // on one shared budget — see signatureTextOf in ./burst-throttle. It also takes
    // `additionalArgs`, because a structured call site keeps its message
    // constant and varies only the payload.
    //
    // `additionalArgs` and NOT the whole context: ambient `enrich` output and
    // `child()` bindings are excluded on purpose, because an enricher
    // returning a per-call value (an active route, a session id) would hand a
    // looping call site a fresh budget every iteration and defeat the cap.
    //
    // The budget is spent here, at the attempt, rather than after a successful
    // `datadogLogs.logger.*` call. That is the correct side for this limiter:
    // it bounds how much we ASK the SDK to ingest, and a throw below means the
    // SDK is failing for every line alike, so preserving budget would buy
    // nothing. It also keeps `dropped_in_previous_window` honest — only lines
    // past the cap count toward it, and those were suppressed here, never by a
    // failed send.
    const verdict = admitLogLine(
      buildLogSignature(level, message, context?.additionalArgs),
      Date.now()
    );
    if (!verdict.keep) {
      return;
    }

    // Formatting stays inside this same `try`: an unserializable message (a
    // circular structure) must drop the record rather than throw into the
    // ~800 call sites of the logger singleton. `signatureTextOf` above
    // tolerates the same input independently, so a circular message is signed,
    // admitted, and then dropped here.
    const scrubStats = createScrubStats();
    const messageForDatadog = formatMessageForDatadog(message, scrubStats);

    const scribe_session_id = getSessionIdFromUrl();

    const browserInfo =
      typeof window !== 'undefined'
        ? {
            browser: platform.name,
            browser_version: platform.version,
            os: platform.os?.family,
            os_version: platform.os?.version,
            user_agent: navigator.userAgent,
          }
        : {};

    // Injected by app wiring (G1/F5) — the pre-move module read
    // NEXT_PUBLIC_BUNDLE_ID / GIT_COMMIT / VERCEL_DEPLOYMENT_ID here.
    const deploymentInfo = {
      bundle_id: bundleId,
      git_commit: gitCommit,
      deployment_id: deploymentId,
    };

    const cleanDeploymentInfo = Object.fromEntries(
      Object.entries(deploymentInfo).filter(([, value]) => value !== undefined)
    );

    // Credentials are scrubbed on the app logger's transmit path, so a client
    // without its own header redaction cannot ship a bearer through
    // `logger.*`. Not a PHI scrub, and not the RUM or SDK-forwarded paths.
    const safeContext = scrubCredentials(context || {}, scrubStats) as LogContext;
    const logContext = {
      ...safeContext,
      ...(scribe_session_id ? { scribe_session_id } : {}),
      ...browserInfo,
      ...cleanDeploymentInfo,
      // Stamped on the first admitted line of the next window, so the volume
      // is dropped but the fact that it happened is not.
      ...(verdict.droppedInPreviousWindow > 0
        ? { dropped_in_previous_window: verdict.droppedInPreviousWindow }
        : {}),
      // Counts only, so the scrub firing (or truncating) is queryable.
      ...(scrubStats.redacted + scrubStats.omitted + scrubStats.truncated > 0
        ? { log_scrub: scrubStats }
        : {}),
      // Lines dropped by a throw below since the last line that made it out.
      ...(failedSinceLastLine > 0 ? { failed_since_last_line: failedSinceLastLine } : {}),
    };
    failedSinceLastLine = 0;

    switch (level) {
      case 'trace':
      case 'debug':
        datadogLogs.logger.debug(messageForDatadog, logContext);
        break;
      case 'info':
        datadogLogs.logger.info(messageForDatadog, logContext);
        break;
      case 'warn':
        datadogLogs.logger.warn(messageForDatadog, logContext);
        break;
      case 'error':
      case 'fatal':
        datadogLogs.logger.error(messageForDatadog, logContext);
        break;
    }
  } catch (e) {
    failedSinceLastLine += 1;
    if (!isProd) {
      originalConsoleMethods.error('Error sending logs to Datadog:', e);
    }
  }
}

// An Error message is sent as its classification only — name, code, status,
// method and query-less path — since its own message and an AxiosError's
// request can carry PHI. Other objects are scrubbed, then stringified.
function formatMessageForDatadog(message: unknown, stats: ScrubStats): string {
  if (isAxiosError(message)) {
    stats.omitted += 1;
    return joinParts([
      message.name,
      message.code,
      message.response?.status,
      message.config?.method?.toUpperCase(),
      scrubCredentialString(message.config?.url?.split(/[?#]/)[0] ?? ''),
    ]);
  }
  if (message instanceof Error) {
    stats.omitted += 1;
    return joinParts([message.name]);
  }
  const safeMessage = scrubCredentials(message, stats);
  if (typeof safeMessage === 'string') return safeMessage;
  if (safeMessage === null) return 'null';
  if (safeMessage === undefined) return 'undefined';
  return JSON.stringify(safeMessage);
}

const joinParts = (parts: unknown[]): string =>
  parts
    .filter((part) => (typeof part === 'string' && part) || typeof part === 'number')
    .join(' ') || 'Error';

function getSessionIdFromUrl(): string | undefined {
  if (typeof window === 'undefined') return undefined;

  const path = window.location.pathname;
  const sessionMatch = path.match(/\/scribe\/sessions?\/([^/#?]+)/);

  return sessionMatch ? sessionMatch[1] : undefined;
}

// Defensive SDK-presence check. The pre-move version also keyed off
// NEXT_PUBLIC_DATADOG_CLIENT_TOKEN presence — that signal now arrives as the
// explicit `enabled` input (checked by the caller above), because key
// presence alone must never activate transmission (ON-PREM-GATE).
const isDatadogAvailable = (): boolean => {
  try {
    return typeof datadogLogs !== 'undefined';
  } catch {
    return false;
  }
};
